import { Bell } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import { format } from "date-fns";

const eventColors = [
  { value: "#6366f1", label: "Indigo" },
  { value: "#10b981", label: "Emerald" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#ef4444", label: "Red" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#06b6d4", label: "Cyan" },
];

const reminderOptions = [
  { value: "15min", label: "15 minutes before" },
  { value: "30min", label: "30 minutes before" },
  { value: "1hour", label: "1 hour before" },
  { value: "1day", label: "1 day before" },
];

export function EventFormDialog({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  isEditing,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-['Outfit']">
            {isEditing ? "Edit Event" : "New Event"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Title</label>
            <Input
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="Event title"
              className="bg-slate-950 border-slate-800"
              data-testid="event-title-input"
            />
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Add details..."
              className="bg-slate-950 border-slate-800 min-h-[60px]"
              data-testid="event-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Date</label>
              <Input
                type="date"
                value={format(formData.date, "yyyy-MM-dd")}
                onChange={(e) =>
                  setFormData({ ...formData, date: new Date(e.target.value) })
                }
                className="bg-slate-950 border-slate-800"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Time</label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) =>
                  setFormData({ ...formData, time: e.target.value })
                }
                className="bg-slate-950 border-slate-800"
                data-testid="event-time-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Color</label>
            <div className="flex gap-2">
              {eventColors.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, color: color.value })
                  }
                  className={`w-8 h-8 rounded-full transition-transform ${
                    formData.color === color.value
                      ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110"
                      : ""
                  }`}
                  style={{ backgroundColor: color.value }}
                  data-testid={`event-color-${color.label.toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-slate-400" />
              <span className="text-slate-300">Reminder</span>
            </div>
            <Switch
              checked={formData.reminder}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, reminder: checked })
              }
              data-testid="event-reminder-switch"
            />
          </div>

          {formData.reminder && (
            <Select
              value={formData.reminder_time}
              onValueChange={(val) =>
                setFormData({ ...formData, reminder_time: val })
              }
            >
              <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="event-reminder-time">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reminderOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-slate-700"
              data-testid="event-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-indigo-600 hover:bg-indigo-500"
              data-testid="event-submit-button"
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
